import { CheckCircle2, Cpu, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const BRANDS = [{
  name: "B&D",
  wordmark: "B&D",
  system: "Tri-Tran 433MHz",
  remotes: "TB5 · TB6 · TB7",
  note: "Most common in Sydney homes."
}, {
  name: "Merlin",
  wordmark: "MERLIN",
  system: "Security+ 2.0",
  remotes: "E945M · E960M · C945",
  note: "Rolling code, Australian made."
}, {
  name: "Chamberlain",
  wordmark: "CHAMBERLAIN",
  system: "Security+ rolling code",
  remotes: "G953EV · 84335EML",
  note: "MyQ smart compatible."
}, {
  name: "ATA",
  wordmark: "ATA",
  system: "TrioCode 128",
  remotes: "PTX5v2 · PTX6v3 · PTX7v2",
  note: "Aussie favourite — used by most builders."
}, {
  name: "Steel-Line",
  wordmark: "STEEL-LINE",
  system: "433MHz rolling",
  remotes: "Boss compatible",
  note: "Standard on Steel-Line installs."
}, {
  name: "LiftMaster",
  wordmark: "LiftMaster",
  system: "Security+ 2.0",
  remotes: "894LT · 893MAX",
  note: "MyQ + smart-home ready."
}, {
  name: "Marantec",
  wordmark: "MARANTEC",
  system: "Digital 868MHz",
  remotes: "D302 · D304",
  note: "German engineering, premium homes."
}, {
  name: "Gliderol",
  wordmark: "GLIDEROL",
  system: "Glidermatic GTS",
  remotes: "GTS / TM-series",
  note: "Common on older roller doors."
}];
export default function RemoteBrands() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner gold ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              04 · Brands We Program In-House
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            Every major Aussie motor —
            <br />
            <span style={{
            color: GOLD_DARK
          }}>
              stocked, coded, paired on-site.
            </span>
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "660px"
        }}>
            We carry brand-matched remotes for the 8 most-installed openers in
            Sydney homes, plus universal remotes for everything older or
            obscure. If it's a garage door — we'll program it.
          </p>
        </div>

        {/* Brands grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BRANDS.map(brand => <article key={brand.name} className="relative rounded-xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-xl" style={{
          backgroundColor: "#ffffff",
          border: `1px solid rgba(8,20,56,0.08)`,
          boxShadow: "0 4px 12px rgba(8,20,56,0.04)"
        }}>
              {/* Gold corner accent */}
              <div aria-hidden className="absolute top-0 right-0 w-12 h-12 pointer-events-none" style={{
            background: `linear-gradient(225deg, ${GOLD}22 0%, transparent 60%)`
          }} />

              <div className="p-6">
                {/* Wordmark */}
                <div className="mb-4 pb-4 border-b" style={{
              borderColor: "rgba(8,20,56,0.08)"
            }}>
                  <div style={{
                fontFamily: FONT_HEAD,
                fontWeight: 900,
                fontSize: "1.4rem",
                letterSpacing: "-0.02em",
                color: NAVY,
                marginBottom: "6px",
                lineHeight: 1.1
              }}>
                    {brand.wordmark}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <CheckCircle2 size={12} color={GOLD_DARK} strokeWidth={2.75} />
                    </span>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: GOLD_DARK
                }}>
                      We program this
                    </span>
                  </div>
                </div>

                {/* System / chip */}
                <div className="mb-3">
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: INK_SOFT,
                marginBottom: "4px"
              }}>
                    Code system
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{
                backgroundColor: `${GOLD}10`,
                border: `1px solid ${GOLD}33`
              }}>
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <Cpu size={11} color={GOLD_DARK} strokeWidth={2.5} />
                    </span>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: NAVY
                }}>
                      {brand.system}
                    </span>
                  </div>
                </div>

                {/* Remote models */}
                <div className="mb-3">
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: INK_SOFT,
                marginBottom: "4px"
              }}>
                    Remote models
                  </div>
                  <div style={{
                fontFamily: FONT_BODY,
                fontSize: "13px",
                fontWeight: 600,
                color: INK,
                lineHeight: 1.4
              }}>
                    {brand.remotes}
                  </div>
                </div>

                {/* Note */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "12.5px",
              lineHeight: 1.55,
              color: INK_SOFT,
              fontStyle: "italic"
            }}>
                  {brand.note}
                </p>
              </div>
            </article>)}
        </div>

        {/* Bottom note */}
        <div className="mt-10 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div className="flex items-start gap-4">
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
            width: 44,
            height: 44,
            backgroundColor: `${GOLD}22`,
            border: `1px solid ${GOLD}44`
          }}>
              <Cpu size={20} color={GOLD_DARK} strokeWidth={2.5} />
            </span>
            <div>
              <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.05rem",
              color: NAVY,
              marginBottom: "4px",
              letterSpacing: "-0.01em"
            }}>
                Brand not on the list?
              </h4>
              <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14px",
              color: INK_SOFT,
              lineHeight: 1.55,
              maxWidth: "520px"
            }}>
                We program universals (ATA TrioCodeGDO, Liftmaster Universal,
                Centsys) that cover Sommer, Centurion, Boss, Tilta-Door,
                Stratco, and most legacy motors.
              </p>
            </div>
          </div>

          <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "13px",
          boxShadow: `0 6px 18px ${NAVY}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={14} strokeWidth={2.5} />
            </span>
            Ask about your motor
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={13} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>
    </section>;
}